import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import $api from '../api'

export interface OrderPlaylistDto {
  businessName: string
  activityType: string
  address: string

  males: string
  females: string

  age18_30: string
  age31_45: string
  age46_60: string

  vocalToInstrumental: string
  internationalToLocal: string

  extraClarifications: string
  linkTracksExamples: string

  // как в mobile: массив жанров (строки)
  styles: string[]
}

export type Order = OrderPlaylistDto & {
  _id: string
  playlists?: any[]
  createdAt: number
}

export type OrdersState = {
  allOrders: Order[]
  listLoading: boolean
  createLoading: boolean
  error: string | null
}

type RemoveSongFromDeliveredPlaylistPayload = {
  orderId: string
  playlistId: string
  songId: string
}

type RemoveSongFromDeliveredPlaylistResponse = {
  success: boolean
  playlist: {
    _id: string
    songs: any[]
    songsCount?: number
    totalDuration?: number
    updatedAt?: string
  }
}

export const initialState: OrdersState = {
  allOrders: [],
  listLoading: false,
  createLoading: false,
  error: null,
}

export const attemptGetUserOrders = createAsyncThunk<Order[]>(
  'order/user/list',
  async () => {
    const response = await $api.post('order/user/list', {})
    return response.data
  },
)

export const attemptOrderPlayList = createAsyncThunk<string, OrderPlaylistDto>(
  'order/create',
  async (payload) => {
    const response = await $api.post('order/create', payload)
    return response.data
  },
)

export const attemptRemoveSongFromDeliveredPlaylist = createAsyncThunk<
  RemoveSongFromDeliveredPlaylistResponse,
  RemoveSongFromDeliveredPlaylistPayload
>('order/remove-song', async ({ orderId, playlistId, songId }) => {
  const response = await $api.post(`order/remove-song/${orderId}`, {
    playlistId,
    songId,
  })
  return response.data
})

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearOrdersError(state) {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // list
      .addCase(attemptGetUserOrders.pending, (state) => {
        state.listLoading = true
        state.error = null
      })
      .addCase(attemptGetUserOrders.fulfilled, (state, { payload }) => {
        state.listLoading = false
        state.allOrders = payload ?? []
      })
      .addCase(attemptGetUserOrders.rejected, (state, action) => {
        state.listLoading = false
        state.error = action.error?.message || 'Failed to load orders'
      })

      // create
      .addCase(attemptOrderPlayList.pending, (state) => {
        state.createLoading = true
        state.error = null
      })
      .addCase(attemptOrderPlayList.fulfilled, (state) => {
        state.createLoading = false
      })
      .addCase(attemptOrderPlayList.rejected, (state, action) => {
        state.createLoading = false
        state.error = action.error?.message || 'Failed to create order'
      })

      // remove song from delivered playlist
      .addCase(attemptRemoveSongFromDeliveredPlaylist.pending, (state) => {
        state.error = null
      })
      .addCase(attemptRemoveSongFromDeliveredPlaylist.fulfilled, (state, action) => {
        const { orderId } = action.meta.arg
        const updatedPlaylist = action.payload?.playlist

        if (!updatedPlaylist) {
          return
        }

        const order = state.allOrders.find((item) => String(item._id) === String(orderId))
        if (!order || !Array.isArray(order.playlists)) {
          return
        }

        order.playlists = order.playlists.map((playlist: any) => {
          if (String(playlist?._id) !== String(updatedPlaylist._id)) {
            return playlist
          }

          return {
            ...playlist,
            ...updatedPlaylist,
          }
        })
      })
      .addCase(attemptRemoveSongFromDeliveredPlaylist.rejected, (state, action) => {
        state.error = action.error?.message || 'Failed to remove song from playlist'
      })
  },
})

export const { clearOrdersError } = ordersSlice.actions
export default ordersSlice.reducer
